import { Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Big } from 'big.js';
import { isDefined } from 'ionic-angular/util/util';
import { ISelectOption } from '../../../../core/interfaces/select-option';
import { CustomValidators } from '../../../validators/custom-validators';
import { ControlComponent } from '../../control.component';
import { FeeControl } from '../fee-control.model';
import { IFeeFieldValue } from './fee-control-value.model';
import { FeeFieldSettings } from './fee-control.settings';
import { Checkbox } from 'ionic-angular';

@Component({
    selector: 'fee-field',
    templateUrl: 'fee-control.component.html',
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FeeControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => FeeControlComponent), multi: true}
    ]
})
export class FeeControlComponent extends ControlComponent implements ControlValueAccessor {
    @Input('value') _value: IFeeFieldValue = {} as any;
    @Input() definition: FeeControl;
    @Input() formControl: FormControl;
    @Input() formGroup: FormGroup;
    @Input() assetId: string;
    @Output() changes: EventEmitter<any> = new EventEmitter();

    // a list of VAT options
    vatOptions: Array<ISelectOption> = [];

    // a list of currency options
    currencyOptions: Array<ISelectOption> = [];

    // a form group for storing the field's value
    calculator: FormGroup;

    constructor(
        protected elemRef: ElementRef
    ) {
        super(elemRef);
    }

    /**
     * Initialises the FormGroup used for storing the fields values
     */
    ngOnInit(): void {

        super.ngOnInit();
        this.currencyOptions = FeeFieldSettings.currencyOptions;
        this.vatOptions = FeeFieldSettings.vatOptions;

        this.calculator = new FormGroup({
            fee: new FormControl(this._value.fee, [CustomValidators.currency]),
            expenses: new FormControl(this._value.expenses, [CustomValidators.currency]),
            exVat: new FormControl(this._value.exVat),
            vatRate: new FormControl(this._value.vatRate),
            currency: new FormControl(this._value.currency),
            format: new FormControl(this._value.format)
        });

        this.calculator.valueChanges.subscribe(
            () => {
                this.updateValue(this.calculator.getRawValue());
            }
        );
    }

    /**
     * Sets the initial value of the field
     * @param value
     */
    writeValue(value: any = {}): void {
        this.setVat(this.prepareValue(value.vatRate, this.definition.config.vatRate, 0.2));
        this.setCurrency(this.prepareValue(value.currency, this.definition.config.currency, 'GBP'));
        this.setExVat(this.prepareValue(value.exVat, this.definition.config.exVat, true));
        this.setFee(this.prepareValue(value.fee, this.definition.config.fee, 0));
        this.setExpenses(this.prepareValue(value.expenses, this.definition.config.expenses, 0));
        this.setFormat(this.prepareValue(value.format, this.definition.config.format, 'GB'));
    }

    /**
     * Returns the first defined value by checking 'value', 'configValue', 'defaultValue'
     * @param value
     * @param configValue
     * @param defaultValue
     */
    protected prepareValue(value, configValue, defaultValue): any {

        if (isDefined(value)) {
            return value;
        } else if (isDefined(configValue)) {
            return configValue;
        }
        return defaultValue;
    }

    /**
     * Sets the fee
     * @param value
     */
    setFee(value: number): void {
        this.calculator.get('fee').setValue(value);
    }

    /**
     * Sets the expenses
     * @param value
     */
    setExpenses(value: number): void {
        this.calculator.get('expenses').setValue(value);
    }

    /**
     * Returns the fee
     * @returns {number}
     */
    getFee(): number {
        return this.calculator.get('fee').value || 0;
    }

    /**
     * Returns the expenses
     * @return {number}
     */
    getExpenses(): number {
        return this.calculator.get('expenses').value || 0;
    }

    /**
     * Sets the excluding vat
     * @param value
     */
    setExVat(value: boolean): void {
        this.calculator.get('exVat').setValue(value);
    }

    /**
     * Returns the excluding vat
     * @returns {boolean}
     */
    getExVat(): boolean {
        return this.calculator.get('exVat').value;
    }

    /**
     * Sets the currency
     * @param value
     */
    setCurrency(value: string): void {
        this.calculator.get('currency').setValue(value);
    }

    /**
     * Returns the currency
     * @returns {string}
     */
    getCurrency(): string {
        return this.calculator.get('currency').value;
    }

    /**
     * Sets the vat rate
     * @param value
     */
    setVat(value: number | string): void {
        this.calculator.get('vatRate').setValue(value);
    }

    /**
     * Returns the vat rate
     * @returns {number}
     */
    getVatRate(): number | string {
        return this.calculator.get('vatRate').value;
    }

    /**
     * Sets the number format
     * @param value
     */
    setFormat(value: string): void {
        this.calculator.get('format').setValue(value);
    }

    /**
     * Gets the number format
     * @returns {any}
     */
    getFormat(): string {
        return this.calculator.get('format').value;
    }

    /**
     * Returns the currency symbol for the selected currency
     * @returns {string}
     */
    getCurrencySymbol(): string {

        const option = this.currencyOptions.find(o => o.value === this.getCurrency());

        if (!option) {
            return '';
        }

        return option.label;
    }

    /**
     * Calculates the sub total
     * @returns {string}
     */
    getSubTotal(): string {

        try {
            const fee = new Big(this.getFee());
            const expenses = new Big(this.getExpenses());
            const vatRate = new Big(this.getVatRate() !== 'n/a' ? this.getVatRate() : 0);
            const net = fee.add(expenses);

            if (this.getExVat()) {
                return net.toFixed(2).toString();
            }
            return net.div(vatRate.plus(1)).toFixed(2).toString();
        } catch (e) {
            return '0.00';
        }

    }

    /**
     * Calculates the vat total
     * @returns {string}
     */
    getVatTotal(): string {

        try {
            const net = new Big(this.getSubTotal());
            const vatRate = new Big(this.getVatRate() !== 'n/a' ? this.getVatRate() : 0);
            return net.mul(vatRate).toFixed(2).toString();
        } catch (e) {
            return '0.00';
        }
    }

    /**
     * Calculates the total
     * @returns {string}
     */
    getTotal(): string {

        try {
            const net = new Big(this.getSubTotal());
            const vat = new Big(this.getVatTotal());
            return net.add(vat).toFixed(2).toString();
        } catch (e) {
            return '0.00';
        }
    }

    /**
     * Returns the placeholder
     * @returns {string}
     */
    getPlaceHolder(): string {
        return this.getConfigOption('placeholder', 'Fee');
    }

    getNumberFormat(): string {
        return this.definition.config.format || 'GB';
    }

    hasFeeError(): boolean {
        return Object.keys(this.getFeeErrors()).length > 0;
    }

    hasExpensesError(): boolean {
        return Object.keys(this.getExpensesErrors()).length > 0;
    }

    getFeeErrors(): {[key: string]: Error} {
        return this.calculator.get('fee').errors || {};
    }

    getExpensesErrors(): {[key: string]: Error} {
        return this.calculator.get('expenses').errors || {};
    }

    getFeeErrorMessage(): string {

        const errors = this.getFeeErrors();

        let message = '';

        Object.keys(errors).forEach(key => {
            message = errors[key].message;
        });

        return message;
    }

    getExpensesErrorMessage(): string {

        const errors = this.getExpensesErrors();

        let message = '';

        Object.keys(errors).forEach(key => {
            message = errors[key].message;
        });

        return message;
    }
}




// WEBPACK FOOTER //
// ./src/vas/controls/fee/form/fee-control.component.ts